import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowBendUpRight, Check, CheckCircle, Folder, X } from '@phosphor-icons/react';
import { ImmoButton } from '../../../../components/immobilien/ui';
import type { BaumKnoten } from '../../../../lib/dokumente/aktenschrankApi';
import { ordnerFarbe } from './dateiDarstellung';

interface Zeile { knoten: BaumKnoten; tiefe: number }
const flach = (liste: readonly BaumKnoten[], tiefe = 0): Zeile[] => liste.flatMap(k => [{ knoten: k, tiefe }, ...flach(k.kinder, tiefe + 1)]);

/**
 * Verschieben (Handoff Dokumente §6.2): Zielordner nur im selben Objekt (Gleich-Objekt-Guard auch im Backend).
 * Der aktuelle Ordner ist ausgegraut.
 */
export function VerschiebenDialog({ dateiName, baum, aktuellerOrdnerId, laeuft, onVerschieben, onAbbrechen }: {
  dateiName: string;
  baum: readonly BaumKnoten[];
  aktuellerOrdnerId: string | null;
  laeuft: boolean;
  onVerschieben: (ziel: BaumKnoten) => void;
  onAbbrechen: () => void;
}) {
  const [ziel, setZiel] = useState<BaumKnoten | null>(null);
  const zeilen = useMemo(() => flach(baum), [baum]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !laeuft) onAbbrechen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onAbbrechen, laeuft]);

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 450, background: 'rgba(0,0,0,0.35)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={e => { if (e.target === e.currentTarget && !laeuft) onAbbrechen(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="verschieben-titel" className="immo-glas-stark immo-side-in flex flex-col w-full"
        style={{ maxWidth: 460, maxHeight: 'calc(100dvh - 32px)', borderRadius: 26, padding: 20, gap: 12, color: 'var(--ink)' }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--acc-soft)' }}>
            <ArrowBendUpRight weight="fill" size={19} color="var(--acc)" />
          </span>
          <span id="verschieben-titel" className="flex-1" style={{ fontSize: 18, fontWeight: 800 }}>Verschieben</span>
          <button type="button" aria-label="Schließen" onClick={onAbbrechen} disabled={laeuft} className="flex items-center justify-center cursor-pointer"
            style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
            <X weight="bold" size={13} />
          </button>
        </div>
        <span className="truncate" style={{ fontSize: 13, color: 'var(--ink2)' }} title={dateiName}>{dateiName}</span>
        <ul className="immo-innen immo-scroll m-0 list-none flex flex-col overflow-auto" style={{ borderRadius: 18, padding: 6, gap: 1, minHeight: 120 }}>
          {zeilen.length === 0 && <li style={{ padding: 14, fontSize: 13, color: 'var(--ink3)' }}>Für dieses Objekt sind keine Ordner bekannt.</li>}
          {zeilen.map(({ knoten: k, tiefe }) => {
            const hier = k.gdriveId === aktuellerOrdnerId;
            const an = ziel?.gdriveId === k.gdriveId;
            return (
              <li key={k.gdriveId}>
                <button type="button" disabled={hier || laeuft} onClick={() => setZiel(k)} aria-pressed={an}
                  className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
                  style={{ gap: 8, minHeight: 40, padding: `0 10px 0 ${8 + tiefe * 16}px`, borderRadius: 11, border: 'none', fontSize: 13, fontWeight: an ? 800 : 600,
                    background: an ? 'var(--acc-soft)' : 'transparent', color: 'var(--ink)', opacity: hier ? 0.45 : 1, cursor: hier ? 'default' : 'pointer' }}>
                  <Folder weight="fill" size={15} color={ordnerFarbe(k.pfad).fg} style={{ flexShrink: 0 }} />
                  <span className="flex-1 min-w-0 truncate">{k.name}</span>
                  {an && <CheckCircle weight="fill" size={15} color="var(--acc)" />}
                </button>
              </li>
            );
          })}
        </ul>
        <span style={{ fontSize: 12, color: ziel ? 'var(--ink2)' : 'var(--ink3)' }}>
          {ziel ? `Ziel: ${ziel.pfad.split('/').join(' › ')}` : 'Bitte einen Zielordner wählen.'}
        </span>
        <div className="flex" style={{ gap: 10 }}>
          <ImmoButton art="fuellung" groesse={44} pille={false} onClick={onAbbrechen} disabled={laeuft} style={{ flex: 1 }}>Abbrechen</ImmoButton>
          <ImmoButton art="akzent" groesse={44} pille={false} icon={Check} disabled={!ziel || laeuft} onClick={() => ziel && onVerschieben(ziel)} style={{ flex: 1 }}>
            {laeuft ? 'Bitte warten …' : 'Verschieben'}
          </ImmoButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
